"use client";

/**
 * ChatbotFolderItem Component
 *
 * Displays a folder in the chatbot sidebar with drag-and-drop support.
 * Similar to FolderItem but for chatbot organization.
 */
import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import type { ChatbotFolderWithChildren } from "@/types/chatbot";
import { ChatbotItem } from "./ChatbotItem";
type ChatbotFolderItemProps = {
  folder: ChatbotFolderWithChildren;
  depth: number;
  selectedChatbotId: string | null;
  onSelectChatbot: (id: string) => void;
  onDeleteChatbot: (id: string) => Promise<void>;
  onRenameChatbot: (id: string, newName: string) => Promise<void>;
  onUpdateFolder: (id: string, updates: {
    name?: string;
    color?: string;
    is_collapsed?: boolean;
  }) => Promise<boolean | void>;
  onDeleteFolder: (id: string) => Promise<boolean | void>;
  onToggleFolderCollapse: (folderId: string) => Promise<boolean | void>;
  onCreateSubfolder: (parentId: string) => void;
  isOver?: boolean;
  isCreatingSubfolder?: boolean;
  newFolderName?: string;
  onNewFolderNameChange?: (name: string) => void;
  onNewFolderKeyDown?: (e: React.KeyboardEvent) => void;
  onNewFolderBlur?: () => void;
  newFolderInputRef?: React.RefObject<HTMLInputElement | null>;
  onStartChatbotThread?: (chatbotId: string, chatbotName: string) => void;
  onEditChatbot?: (id: string) => void;
  onDuplicateChatbot?: (id: string) => void;
};
export function ChatbotFolderItem(t0) {
  const $ = _c(119);
  const {
    folder,
    depth,
    selectedChatbotId,
    onSelectChatbot,
    onDeleteChatbot,
    onRenameChatbot,
    onUpdateFolder,
    onDeleteFolder,
    onToggleFolderCollapse,
    onCreateSubfolder,
    isOver,
    isCreatingSubfolder,
    newFolderName,
    onNewFolderNameChange,
    onNewFolderKeyDown,
    onNewFolderBlur,
    newFolderInputRef,
    onStartChatbotThread,
    onEditChatbot,
    onDuplicateChatbot
  } = t0;
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(folder.name);
  const [showContextMenu, setShowContextMenu] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [contextMenuPos, setContextMenuPos] = useState(t1);
  const editInputRef = useRef(null);
  const contextMenuRef = useRef(null);
  let t2;
  if ($[1] !== folder) {
    t2 = {
      type: "folder",
      folder
    };
    $[1] = folder;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== folder.id || $[4] !== t2) {
    t3 = {
      id: folder.id,
      data: t2
    };
    $[3] = folder.id;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    setNodeRef: setDroppableRef,
    isOver: isDroppableOver
  } = useDroppable(t3);
  let t4;
  if ($[6] !== folder) {
    t4 = {
      type: "folder",
      folder
    };
    $[6] = folder;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== folder.id || $[9] !== t4) {
    t5 = {
      id: folder.id,
      data: t4
    };
    $[8] = folder.id;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    transform,
    isDragging
  } = useDraggable(t5);
  let t6;
  if ($[11] !== setDraggableRef || $[12] !== setDroppableRef) {
    t6 = node => {
      setDroppableRef(node);
      setDraggableRef(node);
    };
    $[11] = setDraggableRef;
    $[12] = setDroppableRef;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const setNodeRef = t6;
  const showDropHighlight = isOver || isDroppableOver;
  let t7;
  if ($[14] !== isDragging || $[15] !== transform) {
    t7 = transform ? {
      transform: CSS.Translate.toString(transform),
      opacity: isDragging ? 0.5 : 1
    } : undefined;
    $[14] = isDragging;
    $[15] = transform;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const style = t7;
  let t8;
  let t9;
  if ($[17] !== isEditing) {
    t8 = () => {
      if (isEditing && editInputRef.current) {
        editInputRef.current.focus();
        editInputRef.current.select();
      }
    };
    t9 = [isEditing];
    $[17] = isEditing;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[20] !== showContextMenu) {
    t10 = () => {
      const handleClickOutside = event => {
        if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
          setShowContextMenu(false);
        }
      };
      if (showContextMenu) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }
    };
    t11 = [showContextMenu];
    $[20] = showContextMenu;
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  useEffect(t10, t11);
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e => {
      e.preventDefault();
      e.stopPropagation();
      setContextMenuPos({
        x: e.clientX,
        y: e.clientY
      });
      setShowContextMenu(true);
    };
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleContextMenu = t12;
  let t13;
  if ($[24] !== folder.name) {
    t13 = e_0 => {
      e_0?.stopPropagation();
      setShowContextMenu(false);
      setEditedName(folder.name);
      setIsEditing(true);
    };
    $[24] = folder.name;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleStartEdit = t13;
  let t14;
  if ($[26] !== editedName || $[27] !== folder.id || $[28] !== folder.name || $[29] !== onUpdateFolder) {
    t14 = async () => {
      const trimmedName = editedName.trim();
      if (trimmedName && trimmedName !== folder.name) {
        await onUpdateFolder(folder.id, {
          name: trimmedName
        });
      }
      setIsEditing(false);
    };
    $[26] = editedName;
    $[27] = folder.id;
    $[28] = folder.name;
    $[29] = onUpdateFolder;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const handleSaveEdit = t14;
  let t15;
  if ($[31] !== folder.name || $[32] !== handleSaveEdit) {
    t15 = e_1 => {
      if (e_1.key === "Enter") {
        e_1.preventDefault();
        handleSaveEdit();
      } else {
        if (e_1.key === "Escape") {
          setIsEditing(false);
          setEditedName(folder.name);
        }
      }
    };
    $[31] = folder.name;
    $[32] = handleSaveEdit;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const handleKeyDown = t15;
  let t16;
  if ($[34] !== folder.chatbots.length || $[35] !== folder.children.length || $[36] !== folder.id || $[37] !== folder.name || $[38] !== onDeleteFolder) {
    t16 = async () => {
      setShowContextMenu(false);
      const hasContent = folder.children.length > 0 || folder.chatbots.length > 0;
      const message = hasContent ? `Delete "${folder.name}" and all its contents?\n\nThis folder contains ${folder.chatbots.length} chatbot(s) and ${folder.children.length} subfolder(s). Chatbots will be moved to the root level.` : `Delete "${folder.name}"?`;
      if (window.confirm(message)) {
        await onDeleteFolder(folder.id);
      }
    };
    $[34] = folder.chatbots.length;
    $[35] = folder.children.length;
    $[36] = folder.id;
    $[37] = folder.name;
    $[38] = onDeleteFolder;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const handleDelete = t16;
  let t17;
  if ($[40] !== folder.id || $[41] !== onCreateSubfolder) {
    t17 = () => {
      setShowContextMenu(false);
      onCreateSubfolder(folder.id);
    };
    $[40] = folder.id;
    $[41] = onCreateSubfolder;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const handleCreateSubfolder = t17;
  const paddingLeft = depth * 12 + 8;
  const t18 = `group flex items-center gap-1 rounded-md px-2 py-1.5 transition-colors cursor-grab active:cursor-grabbing ${showDropHighlight ? "bg-sky/20 ring-2 ring-sky/50" : "hover:bg-white/40"} ${isDragging ? "z-50" : ""}`;
  let t19;
  if ($[43] !== paddingLeft) {
    t19 = {
      paddingLeft
    };
    $[43] = paddingLeft;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== folder.id || $[46] !== onToggleFolderCollapse) {
    t20 = e_2 => {
      e_2.stopPropagation();
      onToggleFolderCollapse(folder.id);
    };
    $[45] = folder.id;
    $[46] = onToggleFolderCollapse;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  const t21 = `h-3 w-3 transition-transform ${folder.is_collapsed ? "" : "rotate-90"}`;
  let t22;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t21) {
    t23 = <svg className={t21} fill="currentColor" viewBox="0 0 20 20">{t22}</svg>;
    $[49] = t21;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t20 || $[52] !== t23) {
    t24 = <button type="button" onClick={t20} className="flex-shrink-0 p-0.5 text-foreground/50 hover:text-foreground">{t23}</button>;
    $[51] = t20;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const t25 = `h-4 w-4 flex-shrink-0 ${folder.color ? "" : "text-amber-400"}`;
  let t26;
  if ($[54] !== folder.color) {
    t26 = folder.color ? {
      color: folder.color
    } : undefined;
    $[54] = folder.color;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t25 || $[58] !== t26) {
    t28 = <svg className={t25} style={t26} fill="currentColor" viewBox="0 0 20 20">{t27}</svg>;
    $[57] = t25;
    $[58] = t26;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== editedName || $[61] !== folder.name || $[62] !== handleKeyDown || $[63] !== handleSaveEdit || $[64] !== handleStartEdit || $[65] !== isEditing) {
    t29 = isEditing ? <input ref={editInputRef} type="text" value={editedName} onChange={e_3 => setEditedName(e_3.target.value)} onKeyDown={handleKeyDown} onBlur={handleSaveEdit} onClick={_temp} className="flex-1 rounded border border-white/40 bg-white/60 px-1.5 py-0.5 text-sm text-foreground focus:border-sky focus:outline-none focus:ring-1 focus:ring-sky" /> : <span className="flex-1 truncate text-sm text-foreground" onDoubleClick={handleStartEdit}>{folder.name}</span>;
    $[60] = editedName;
    $[61] = folder.name;
    $[62] = handleKeyDown;
    $[63] = handleSaveEdit;
    $[64] = handleStartEdit;
    $[65] = isEditing;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== handleCreateSubfolder || $[68] !== handleDelete || $[69] !== handleStartEdit || $[70] !== isEditing) {
    t30 = !isEditing && <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><button type="button" onClick={handleCreateSubfolder} className="rounded p-1 hover:bg-white/40 text-foreground/50 hover:text-foreground" title="Create subfolder"><svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg></button><button type="button" onClick={handleStartEdit} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-sky" title="Rename folder"><svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg></button><button type="button" onClick={handleDelete} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-red-400" title="Delete folder"><svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg></button></div>;
    $[67] = handleCreateSubfolder;
    $[68] = handleDelete;
    $[69] = handleStartEdit;
    $[70] = isEditing;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== folder.chatbots.length) {
    t31 = folder.chatbots.length > 0 && <span className="text-xs text-foreground/50 tabular-nums">{folder.chatbots.length}</span>;
    $[72] = folder.chatbots.length;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== attributes || $[75] !== listeners || $[76] !== t18 || $[77] !== t19 || $[78] !== t24 || $[79] !== t28 || $[80] !== t29 || $[81] !== t30 || $[82] !== t31) {
    t32 = <div onContextMenu={handleContextMenu} className={t18} style={t19} {...attributes} {...listeners}>{t24}{t28}{t29}{t30}{t31}</div>;
    $[74] = attributes;
    $[75] = listeners;
    $[76] = t18;
    $[77] = t19;
    $[78] = t24;
    $[79] = t28;
    $[80] = t29;
    $[81] = t30;
    $[82] = t31;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== contextMenuPos || $[85] !== handleCreateSubfolder || $[86] !== handleDelete || $[87] !== handleStartEdit || $[88] !== showContextMenu) {
    t33 = showContextMenu && <div ref={contextMenuRef} className="fixed z-50 rounded-md border border-white/40 bg-white/80 backdrop-blur-md shadow-lg py-1 min-w-[160px]" style={{
      left: contextMenuPos.x,
      top: contextMenuPos.y
    }}><button onClick={handleCreateSubfolder} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-white/40"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>New Subfolder</button><button onClick={handleStartEdit} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-white/40"><svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>Rename</button><div className="my-1 border-t border-white/40" /><button onClick={handleDelete} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-400 hover:bg-white/40"><svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>Delete</button></div>;
    $[84] = contextMenuPos;
    $[85] = handleCreateSubfolder;
    $[86] = handleDelete;
    $[87] = handleStartEdit;
    $[88] = showContextMenu;
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  let t34;
  if ($[90] !== depth || $[91] !== folder.chatbots || $[92] !== folder.children || $[93] !== folder.is_collapsed || $[94] !== isCreatingSubfolder || $[95] !== newFolderInputRef || $[96] !== newFolderName || $[97] !== onCreateSubfolder || $[98] !== onDeleteChatbot || $[99] !== onDeleteFolder || $[100] !== onDuplicateChatbot || $[101] !== onEditChatbot || $[102] !== onNewFolderBlur || $[103] !== onNewFolderKeyDown || $[104] !== onNewFolderNameChange || $[105] !== onRenameChatbot || $[106] !== onSelectChatbot || $[107] !== onStartChatbotThread || $[108] !== onToggleFolderCollapse || $[109] !== onUpdateFolder || $[110] !== paddingLeft || $[111] !== selectedChatbotId) {
    t34 = !folder.is_collapsed && <div>{isCreatingSubfolder && <div className="flex items-center gap-1 px-2 py-1" style={{
        paddingLeft: paddingLeft + 24
      }}><svg className="h-4 w-4 text-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg><input ref={newFolderInputRef} type="text" value={newFolderName || ""} onChange={e_5 => onNewFolderNameChange?.(e_5.target.value)} onKeyDown={onNewFolderKeyDown} onBlur={onNewFolderBlur} placeholder="Folder name" className="flex-1 rounded border border-white/40 bg-white/60 px-2 py-1 text-sm text-foreground focus:border-sky focus:outline-none focus:ring-1 focus:ring-sky" /></div>}{folder.children.map(child => <ChatbotFolderItem key={child.id} folder={child} depth={depth + 1} selectedChatbotId={selectedChatbotId} onSelectChatbot={onSelectChatbot} onDeleteChatbot={onDeleteChatbot} onRenameChatbot={onRenameChatbot} onUpdateFolder={onUpdateFolder} onDeleteFolder={onDeleteFolder} onToggleFolderCollapse={onToggleFolderCollapse} onCreateSubfolder={onCreateSubfolder} isCreatingSubfolder={isCreatingSubfolder} newFolderName={newFolderName} onNewFolderNameChange={onNewFolderNameChange} onNewFolderKeyDown={onNewFolderKeyDown} onNewFolderBlur={onNewFolderBlur} newFolderInputRef={newFolderInputRef} onStartChatbotThread={onStartChatbotThread} onEditChatbot={onEditChatbot} onDuplicateChatbot={onDuplicateChatbot} />)}{folder.chatbots.map(chatbot => <ChatbotItem key={chatbot.id} chatbot={chatbot} isSelected={chatbot.id === selectedChatbotId} onClick={() => onSelectChatbot(chatbot.id)} onStartThread={onStartChatbotThread ? () => onStartChatbotThread(chatbot.id, chatbot.name) : undefined} onEdit={onEditChatbot ? () => onEditChatbot(chatbot.id) : undefined} onDuplicate={onDuplicateChatbot ? () => onDuplicateChatbot(chatbot.id) : undefined} onDelete={() => onDeleteChatbot(chatbot.id)} onRename={newName => onRenameChatbot(chatbot.id, newName)} depth={depth + 1} isDraggable={true} />)}</div>;
    $[90] = depth;
    $[91] = folder.chatbots;
    $[92] = folder.children;
    $[93] = folder.is_collapsed;
    $[94] = isCreatingSubfolder;
    $[95] = newFolderInputRef;
    $[96] = newFolderName;
    $[97] = onCreateSubfolder;
    $[98] = onDeleteChatbot;
    $[99] = onDeleteFolder;
    $[100] = onDuplicateChatbot;
    $[101] = onEditChatbot;
    $[102] = onNewFolderBlur;
    $[103] = onNewFolderKeyDown;
    $[104] = onNewFolderNameChange;
    $[105] = onRenameChatbot;
    $[106] = onSelectChatbot;
    $[107] = onStartChatbotThread;
    $[108] = onToggleFolderCollapse;
    $[109] = onUpdateFolder;
    $[110] = paddingLeft;
    $[111] = selectedChatbotId;
    $[112] = t34;
  } else {
    t34 = $[112];
  }
  let t35;
  if ($[113] !== setNodeRef || $[114] !== style || $[115] !== t32 || $[116] !== t33 || $[117] !== t34) {
    t35 = <div ref={setNodeRef} style={style}>{t32}{t33}{t34}</div>;
    $[113] = setNodeRef;
    $[114] = style;
    $[115] = t32;
    $[116] = t33;
    $[117] = t34;
    $[118] = t35;
  } else {
    t35 = $[118];
  }
  return t35;
}
function _temp(e_4) {
  return e_4.stopPropagation();
}